import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Send, FlaskConical, Loader2, ShieldCheck, AlertTriangle,
} from "lucide-react";

export const KIND_OPTIONS = [
  { value: "boletin", label: "Boletim técnico completo" },
  { value: "alerta", label: "Alerta imediato" },
];

export const WINDOW_OPTIONS = [
  { value: "1", label: "Últimas 24 horas" },
  { value: "7", label: "Últimos 7 dias" },
  { value: "15", label: "Últimos 15 dias" },
  { value: "30", label: "Últimos 30 dias" },
];

export const PREFERENCE_OPTIONS = [
  { key: "boletins", label: "Receber boletins programados" },
  { key: "alertas", label: "Receber alertas imediatos" },
  { key: "pga", label: "Receber informações específicas do PGA" },
];

/** Converte ISO para pt-BR no fuso informado (padrão: Brasília). */
export function fmtDateTime(value, tz = "America/Sao_Paulo") {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      timeZone: tz,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function PreviewFrame({ html }) {
  return (
    <div className="rounded-md border overflow-hidden bg-white">
      <iframe
        title="Pré-visualização da mensagem"
        srcDoc={html || ""}
        className="w-full h-[50vh] bg-white"
        data-testid="send-preview-frame"
      />
    </div>
  );
}

function RecipientPicker({ recipients, selected, onChange }) {
  if (!recipients.length) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
        <AlertTriangle size={15} />
        Unidade sem destinatários ativos e autorizados. Cadastre um
        destinatário na aba Destinatários antes de enviar.
      </div>
    );
  }

  const toggle = (id) => {
    onChange(
      selected.includes(id)
        ? selected.filter((value) => value !== id)
        : [...selected, id],
    );
  };

  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">
        Destinatários autorizados ({selected.length} de {recipients.length})
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-1">
        {recipients.map((item) => (
          <label
            key={item.id}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <Checkbox
              checked={selected.includes(item.id)}
              onCheckedChange={() => toggle(item.id)}
              data-testid={`recipient-${item.id}`}
            />
            <span>{item.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Envio manual ("Enviar agora").
 *
 * Exige pré-visualização completa e confirmação explícita. Nenhum
 * endereço de e-mail é exibido: apenas nome e identificador, para que
 * um destinatário nunca veja o contato de outro.
 */
export function SendNowDialog({
  open, onOpenChange, clientId, unitId, onSent,
}) {
  const [kind, setKind] = useState("boletin");
  const [days, setDays] = useState("7");
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setSelected([]);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/envintel/send-preview", {
          client_id: clientId, unit_id: unitId, kind, days: Number(days),
        });
        if (!active) return;
        setPreview(data);
        setSelected((data.recipients || []).map((r) => r.id));
      } catch (err) {
        if (active) {
          setPreview(null);
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, clientId, unitId, kind, days]);

  const confirm = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/envintel/send-now", {
        client_id: clientId, unit_id: unitId, kind, days: Number(days),
        recipient_ids: selected, confirm: true,
      });
      if (data.ok) {
        toast.success(
          `${data.status_label || "Enviado"} — rastreio ${data.tracking_id}`,
          {
            description: "Aceito pelo serviço de e-mail. A entrega depende "
              + "de confirmação do provedor.",
          },
        );
      } else {
        toast.error(data.status_label || "Falha no envio.");
      }
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const ready = Boolean(preview?.ready) && selected.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar agora</DialogTitle>
          <DialogDescription>
            Confirme os dados antes do envio. O envio é registrado no
            histórico com o usuário responsável.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Tipo de comunicação</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="send-now-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Período de análise</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="send-now-window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOW_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <RecipientPicker
          recipients={preview?.recipients || []}
          selected={selected}
          onChange={setSelected}
        />

        {preview && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck size={14} />
            <span>Unidade: {preview.unit_name}</span>
            <span>·</span>
            <span>Período: {preview.window_label}</span>
            <span>·</span>
            <span>
              Referência da análise: {fmtDateTime(preview.reference_time)}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="animate-spin" size={16} />
            Montando a pré-visualização…
          </div>
        ) : (
          <PreviewFrame html={preview?.html} />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={confirm}
            disabled={!ready || sending}
            data-testid="send-now-confirm"
          >
            {sending
              ? <Loader2 className="animate-spin mr-1" size={15} />
              : <Send className="mr-1" size={15} />}
            Confirmar envio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Envio de teste.
 *
 * Usa a MESMA infraestrutura do envio real, mas a mensagem é identificada
 * como TESTE e fica fora das métricas de boletins dos clientes. O
 * resultado informado é o estágio real: "aceito pelo provedor" não é o
 * mesmo que "entregue", e o sistema não afirma o que não pode provar.
 */
export function TestSendDialog({
  open, onOpenChange, clientId, unitId, defaultKind, onSent,
}) {
  const [kind, setKind] = useState(defaultKind || "boletin");
  const [email, setEmail] = useState("");
  const [recipientIds, setRecipientIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setKind(defaultKind || "boletin");
      setResult(null);
    }
  }, [open, defaultKind]);

  useEffect(() => {
    if (!open || !clientId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/envintel/recipients", {
          params: { client_id: clientId, status: "ativo" },
        });
        if (active) setOptions(data.recipients || []);
      } catch {
        if (active) setOptions([]);
      }
    })();
    return () => { active = false; };
  }, [open, clientId]);

  const toggle = (id) => {
    setRecipientIds((current) => (
      current.includes(id)
        ? current.filter((v) => v !== id)
        : [...current, id]
    ));
  };

  const submit = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/envintel/test-send", {
        client_id: clientId,
        unit_id: unitId,
        kind,
        recipients: email ? [email] : [],
        recipient_ids: recipientIds,
      });
      setResult(data);
      if (data.ok) {
        toast.success(
          `Solicitação registrada — rastreio ${data.tracking_id}`,
        );
      } else {
        toast.error(
          data.status_label || "Não foi possível concluir o teste.",
          { description: `Rastreio: ${data.tracking_id || "—"}` },
        );
      }
      onSent?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const stages = useMemo(() => {
    if (!result) return [];
    return [
      { label: "Solicitação registrada", done: true },
      { label: "Processando envio", done: true },
      {
        label: "Aceito pelo provedor",
        done: ["aceito", "entregue"].includes(result.status),
      },
      { label: "Entregue", done: result.status === "entregue" },
    ];
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Enviar teste</DialogTitle>
          <DialogDescription>
            A mensagem é enviada pela mesma infraestrutura do envio real e
            identificada como TESTE. Não conta como boletim enviado ao
            cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label>Modelo de comunicação</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="test-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="test-email">Endereço que receberá o teste</Label>
          <Input
            id="test-email"
            type="email"
            placeholder="Use um destinatário cadastrado ou seu e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="test-email"
          />
          <p className="text-xs text-muted-foreground">
            O endereço precisa pertencer ao seu escopo de autorização.
          </p>
        </div>

        {options.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Ou selecione destinatários cadastrados:
            </div>
            <div className="max-h-32 overflow-y-auto rounded-md border p-2 space-y-1">
              {options.map((item) => (
                <label
                  key={item.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={recipientIds.includes(item.id)}
                    onCheckedChange={() => toggle(item.id)}
                    data-testid={`test-recipient-${item.id}`}
                  />
                  <span>{item.full_name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-2 rounded-md border p-3 text-sm">
            <div className="font-medium">Resultado do teste</div>
            <ul className="space-y-1">
              {stages.map((stage) => (
                <li key={stage.label} className="flex items-center gap-2">
                  <Badge variant={stage.done ? "default" : "outline"}>
                    {stage.done ? "ok" : "—"}
                  </Badge>
                  <span className={stage.done ? "" : "text-muted-foreground"}>
                    {stage.label}
                  </span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-muted-foreground">
              Status: {result.status_label || result.status} ·
              Rastreio: {result.tracking_id || "—"}
            </div>
            {result.ok && result.status !== "entregue" && (
              <p className="text-xs text-amber-700">
                O provedor aceitou a mensagem. A entrega ainda será
                confirmada pelo provedor — a resposta da API, sozinha, não
                comprova a entrega.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button
            onClick={submit}
            disabled={sending || (!email && !recipientIds.length)}
            data-testid="test-send-confirm"
          >
            {sending
              ? <Loader2 className="animate-spin mr-1" size={15} />
              : <FlaskConical className="mr-1" size={15} />}
            Enviar teste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


